import { TFunction } from "i18next";
import WelcomeBannerWidget from "../WelcomeBannerWidget";
import IotAssetsStatusWidget from "../IotAssetsStatusWidget";
import MapWidget from "../MapWidget";
import KPIWidget from "../KPIWidget";
import AssetMetricsWidget from "../AssetMetricsWidget";
import AssetShortcutsWidget from "../AssetShortcutsWidget";
import QuickActionWidget from "../QuickActionWidget";
import ServiceUnitWidget from "../ServiceUnitWidget";
import { WELCOME_BANNER_TYPE } from "../../config/welcomeCampaign";

export type DashboardArea = 'left' | 'right';
export type WidgetWidth = 1 | 2 | 4;
export type WidgetHeight = 1 | 2 | 3 | 4;

/**
 * Configuration data for a widget instance.
 * Each configurable widget type can define its own config shape.
 */
export interface WidgetConfig {
  [key: string]: unknown;
}

/**
 * A specific instance of a widget on the dashboard.
 * - For singleton widgets: instanceId equals the widget type
 * - For configurable widgets: instanceId is unique (e.g., "count_abc123")
 */
export interface WidgetInstance {
  instanceId: string;
  type: string;
  config?: WidgetConfig;
}

/**
 * Props passed to widget content components.
 */
export interface WidgetContentProps {
  onDelete: () => void;
  onEdit: () => void;
  config?: WidgetConfig;
  instanceId: string;
}

export interface DashboardWidget {
  type: string;
  defaultTitle: string;
  description: string;
  content: (props: WidgetContentProps) => React.ReactNode;
  resource?: string;
  requiresContract?: boolean;
  requiresAccessLevel?: string[];
  area: DashboardArea;
  width: WidgetWidth;
  /**
   * Height multiplier for the widget. 1 = half height, 2 = standard height, 4 = double height.
   * @default 2
   */
  height?: WidgetHeight;
  /**
   * If true, multiple instances of this widget can be added.
   * Each instance can have its own configuration.
   * @default false
   */
  configurable?: boolean;
  /**
   * Options for configurable widgets shown in the modal dropdown.
   * Each option can have an optional resource guard.
   */
  configOptions?: Array<{ value: string; label: string; resource?: string; requiresContract?: boolean; requiresAccessLevel?: string[] }>;
  /**
   * The key in WidgetConfig used to store the selected config option.
   * Required for configurable widgets. Defaults to 'kpiType' for backward compatibility.
   */
  configKey?: string;
  /**
   * If true, the widget renders without border and padding.
   * The widget content fills the entire grid cell.
   */
  borderless?: boolean;
}

/**
 * Generate a unique instance ID for a new widget.
 */
export const generateInstanceId = (type: string): string => {
  return `${type}_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
};

/**
 * Create a widget instance from a widget type.
 * For singleton widgets, the instanceId is the type itself.
 * For configurable widgets, a unique instanceId is generated.
 */
export const createWidgetInstance = (
  widget: DashboardWidget,
  config?: WidgetConfig
): WidgetInstance => {
  if (widget.configurable) {
    return {
      instanceId: generateInstanceId(widget.type),
      type: widget.type,
      config,
    };
  }
  return {
    instanceId: widget.type,
    type: widget.type,
  };
};

export interface WidgetTypeOptions {
  /**
   * Hides the customer-facing "who serves my company" widget. Set for users of
   * an internal (service provider) company and for identities without a company
   * — the resolution chain on their own client answers nothing useful to them.
   */
  hideServiceUnitWidget?: boolean;
  /**
   * Offers the exchange quick action. Only semiconductor users and global
   * admins get it — see canUseExchangeQuickAction.
   */
  canExchangeAssets?: boolean;
}

export const getWidgetTypes = (
  t: TFunction,
  resources: string[],
  contractValid: boolean,
  accessLevel: string = '',
  options: WidgetTypeOptions = {}
): DashboardWidget[] => {
  const widgets: DashboardWidget[] = [
    {
      // Available to every user; placement/height come from
      // src/utils/welcomeBanner.ts, not the generic layout paths.
      type: WELCOME_BANNER_TYPE,
      defaultTitle: t("Welcome Banner"),
      description: t("Introduces the platform and highlights what's new."),
      content: ({ onDelete, onEdit }) => (
          <WelcomeBannerWidget onDelete={onDelete} onEdit={onEdit} />
      ),
      area: 'left',
      width: 4,
      height: 1,
      configurable: false,
      borderless: true,
    },
    {
      type: "IotAssetsStatus",
      defaultTitle: t("IoT Assets Status"),
      description: t("Displays the status of IoT assets, including running, warning, turned off and with alarms."),
      content: ({ onDelete, onEdit }) => (
          <IotAssetsStatusWidget onDelete={onDelete} onEdit={onEdit} />
      ),
      area: 'left',
      width: 2,
      height: 2,
      configurable: false,
      requiresContract: true,
      requiresAccessLevel: ['global', 'region'],
    },
    {
      type: "assetMap",
      defaultTitle: t("Asset Locations"),
      description: t("Displays assets on an interactive map with geo locations."),
      content: ({ onDelete, onEdit }) => (
          <MapWidget onDelete={onDelete} onEdit={onEdit} />
      ),
      area: 'left',
      width: 2,
      height: 3,
      configurable: false,
      borderless: true,
    },
    {
      type: "kpi",
      defaultTitle: t("KPI"),
      description: t("Displays a single KPI metric."),
      content: ({ onDelete, onEdit, config }) => (
          <KPIWidget onDelete={onDelete} onEdit={onEdit} config={config} />
      ),
      area: 'left',
      width: 1,
      height: 1,
      configurable: true,
      configKey: 'kpiType',
      configOptions: [
        { value: 'totalAssetCount', label: t('Total Assets') },
        { value: 'connectedPumps', label: t('Connected Assets'), requiresContract: true, requiresAccessLevel: ['global', 'region'] },
        { value: 'totalCustomers', label: t('Total Companies'), resource: 'administrateCustomers' },
        { value: 'totalUsers', label: t('Total Users'), resource: 'administrateUsers' },
      ],
    },
    {
      type: "assetMetrics",
      defaultTitle: t("Asset Metrics"),
      description: t("Displays asset health and maintenance metric counts."),
      content: ({ onDelete, onEdit, config }) => (
          <AssetMetricsWidget onDelete={onDelete} onEdit={onEdit} config={config} />
      ),
      area: 'left',
      width: 1,
      height: 1,
      configurable: true,
      configKey: 'assetMetricType',
      configOptions: [
        { value: 'harshCondition', label: t('Harsh Condition'), requiresContract: true, requiresAccessLevel: ['global', 'region'] },
        { value: 'maintenanceDueSoon', label: t('Maintenance Due'), requiresContract: true, requiresAccessLevel: ['global', 'region'] },
        { value: 'maintenanceOverdue', label: t('Maintenance Overdue'), requiresContract: true, requiresAccessLevel: ['global', 'region'] },
        { value: 'exhaustFilter', label: t('Exhaust Filter'), requiresContract: true, requiresAccessLevel: ['global', 'region'] },
        { value: 'missingSensor', label: t('Missing Sensor'), requiresContract: true, requiresAccessLevel: ['global', 'region'] },
        { value: 'vsdCandidate', label: t('VSD Candidate'), requiresContract: true, requiresAccessLevel: ['global', 'region'] },
        { value: 'sensorValueOutOfRange', label: t('Sensor Value out of Range'), requiresContract: true, requiresAccessLevel: ['global', 'region'] },
      ],
    },
    {
      type: "assetShortcuts",
      defaultTitle: t("Favorites"),
      description: t("Quick access to your favorites."),
      content: ({ onDelete, onEdit }) => (
          <AssetShortcutsWidget onDelete={onDelete} onEdit={onEdit} />
      ),
      area: 'right',
      width: 2,
      height: 4,
      configurable: false,
    },
    {
      // No resource/contract gate — the resolution chain always answers
      // (IBM-972). Hidden for internal companies via WidgetTypeOptions instead:
      // the answer is customer-facing.
      type: "serviceUnit",
      defaultTitle: t("Service contact"),
      description: t("Shows the Service Center responsible for your company, including its contact details."),
      content: ({ onDelete, onEdit }) => (
          <ServiceUnitWidget onDelete={onDelete} onEdit={onEdit} />
      ),
      area: 'right',
      width: 2,
      height: 3,
      configurable: false,
    },
    {
      type: "quickAction",
      defaultTitle: t("Quick Action"),
      description: t("Perform a quick action such as removing an asset from a location."),
      content: ({ onDelete, onEdit, config }) => (
          <QuickActionWidget onDelete={onDelete} onEdit={onEdit} config={config} />
      ),
      area: 'right',
      width: 1,
      height: 1,
      configurable: true,
      configKey: 'actionType',
      configOptions: [
        { value: 'removeAsset', label: t('Remove asset') },
        { value: 'installAsset', label: t('Install asset') },
        { value: 'exchangeAsset', label: t('Exchange asset') },
      ],
    },
  ];

  return widgets
    .map(w => {
      if (!w.configOptions) return w;
      const filteredOptions = w.configOptions.filter(opt => {
        if (w.type === 'quickAction' && opt.value === 'exchangeAsset' && !options.canExchangeAssets) return false;
        if (opt.resource && !resources.includes(opt.resource)) return false;
        const needsContractOrAccess = opt.requiresContract || (opt.requiresAccessLevel && opt.requiresAccessLevel.length > 0);
        if (needsContractOrAccess) {
          const contractOk = opt.requiresContract ? contractValid : false;
          const accessOk = opt.requiresAccessLevel
            ? opt.requiresAccessLevel.some(l => l.toLowerCase() === accessLevel.toLowerCase())
            : false;
          if (!contractOk && !accessOk) return false;
        }
        return true;
      });
      return { ...w, configOptions: filteredOptions };
    })
    .filter(w => {
      if (w.type === 'serviceUnit' && options.hideServiceUnitWidget) return false;
      if (w.resource && !resources.includes(w.resource)) return false;
      const needsContractOrAccess = w.requiresContract || (w.requiresAccessLevel && w.requiresAccessLevel.length > 0);
      if (needsContractOrAccess) {
        const contractOk = w.requiresContract ? contractValid : false;
        const accessOk = w.requiresAccessLevel
          ? w.requiresAccessLevel.some(l => l.toLowerCase() === accessLevel.toLowerCase())
          : false;
        if (!contractOk && !accessOk) return false;
      }
      if (w.configurable && w.configOptions && w.configOptions.length === 0) return false;
      return true;
    });
};
